// ─── Device detection ─────────────────────────────────────────────────────────
const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

// ─── Android vibration ────────────────────────────────────────────────────────
export const haptic = {
  light:   () => { if (!isIOS && navigator.vibrate) navigator.vibrate(8); },
  medium:  () => { if (!isIOS && navigator.vibrate) navigator.vibrate(18); },
  heavy:   () => { if (!isIOS && navigator.vibrate) navigator.vibrate(35); },
  success: () => { if (!isIOS && navigator.vibrate) navigator.vibrate([10, 30, 10]); },
  error:   () => { if (!isIOS && navigator.vibrate) navigator.vibrate([20, 40, 20, 40, 20]); },
};

// ─── Web Audio context (singleton compartilhado) ─────────────────────────────
import { getSharedCtx } from './sharedAudioCtx';

function getCtx() {
  return getSharedCtx();
}

// Garante que o contexto está rodando — deve ser chamado dentro de um gesto
function _ensureUnlocked() {
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
}

// ─── Sound profiles ───────────────────────────────────────────────────────────
// Each profile: { freqStart, freqEnd, duration, volume, type, hasNoise }
const SOUNDS = {
  // Leve — tabs, ícones, menus
  light: {
    layers: [
      { freqStart: 900,  freqEnd: 380,  dur: 0.048, vol: 0.22, type: 'sine' },
    ],
  },
  // Médio — botões normais (camada única para evitar duplo-som no iOS)
  medium: {
    layers: [
      { freqStart: 1100, freqEnd: 440,  dur: 0.060, vol: 0.32, type: 'sine' },
    ],
  },
  // Pesado — CTAs principais
  heavy: {
    layers: [
      { freqStart: 1200, freqEnd: 320,  dur: 0.080, vol: 0.40, type: 'sine' },
    ],
  },
  // Sucesso — confirmação
  success: {
    layers: [
      { freqStart: 880,  freqEnd: 1320, dur: 0.070, vol: 0.30, type: 'sine' },
    ],
  },
  // Erro
  error: {
    layers: [
      { freqStart: 300,  freqEnd: 140,  dur: 0.090, vol: 0.32, type: 'sawtooth' },
    ],
  },
};

// ─── Play a sound profile ─────────────────────────────────────────────────────
let _lastPlay = 0;
let _lastTouch = 0;
const MIN_INTERVAL_MS = 40;    // evita flood de sons simultâneos
const TOUCH_MOUSE_GAP = 600;   // ignora mousedown que chega após touchstart (iOS)

function _playSoundNow(ctx, profile) {
  const def = SOUNDS[profile] || SOUNDS.medium;
  const pitchFactor = 0.92 + Math.random() * 0.16;

  def.layers.forEach(({ freqStart, freqEnd, dur, vol, type, delay = 0 }) => {
    try {
      const t = ctx.currentTime + delay;
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = type;
      osc.frequency.setValueAtTime(freqStart * pitchFactor, t);
      osc.frequency.exponentialRampToValueAtTime(Math.max(freqEnd * pitchFactor, 20), t + dur);

      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.linearRampToValueAtTime(vol, t + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + dur + 0.01);
    } catch {}
  });
}

export function playSound(profile = 'medium') {
  const now = Date.now();
  if (now - _lastPlay < MIN_INTERVAL_MS) return;
  _lastPlay = now;

  const ctx = getCtx();
  if (!ctx) return;

  // Toca diretamente — se o contexto ainda está suspenso após _ensureUnlocked(),
  // o resume() já foi disparado; tocar agora funciona no iOS pois o buffer
  // silencioso desbloqueou o contexto no mesmo gesto.
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
  _playSoundNow(ctx, profile);
}

// ─── Selectors ────────────────────────────────────────────────────────────────
const BTN_SELECTORS = [
  'button:not([disabled])',
  'a[href]',
  '[role="button"]',
  '.clickable',
  '.MuiButtonBase-root:not(.Mui-disabled)',
  '.MuiButton-root:not(.Mui-disabled)',
  '.MuiIconButton-root:not(.Mui-disabled)',
  '.MuiFab-root',
  '.MuiChip-clickable',
  '.MuiListItemButton-root:not(.Mui-disabled)',
  '.MuiMenuItem-root',
  '.MuiTab-root',
  '.MuiBottomNavigationAction-root',
  '[data-haptic]',
].join(',');

// ─── Classify button strength ─────────────────────────────────────────────────
function getProfile(el) {
  const attr = el.dataset?.haptic;
  if (attr && SOUNDS[attr]) return attr;

  if (
    el.classList.contains('btn-start') ||
    el.dataset?.hapticStrength === 'heavy' ||
    el.closest('[data-haptic="heavy"]')
  ) return 'heavy';

  if (
    el.classList.contains('MuiTab-root') ||
    el.classList.contains('MuiBottomNavigationAction-root') ||
    el.classList.contains('MuiIconButton-root') ||
    el.classList.contains('MuiMenuItem-root') ||
    el.tagName === 'A'
  ) return 'light';

  return 'medium';
}

// ─── Visual press/release (scale + bounce + glow) ─────────────────────────────
const SCALE = { light: 0.975, medium: 0.960, heavy: 0.940 };
const _pressed = new WeakMap();

function press(el, profile) {
  if (_pressed.has(el)) return;
  _pressed.set(el, true);

  const s = SCALE[profile] || 0.96;
  el.style.transition = 'transform 0.08s ease, filter 0.08s ease';
  el.style.transform  = `scale(${s})`;
  el.style.filter     = profile === 'heavy' ? 'brightness(1.12)' : profile === 'medium' ? 'brightness(1.07)' : 'brightness(1.04)';
  el.style.webkitTapHighlightColor = 'transparent';
}

function release(el) {
  if (!_pressed.has(el)) return;
  _pressed.delete(el);

  el.style.transition = 'transform 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275), filter 0.2s ease';
  el.style.transform  = '';
  el.style.filter     = '';
}

// ─── Global init ──────────────────────────────────────────────────────────────
export function initGlobalHaptics() {
  // ── touchstart ─────────────────────────────────────────────────────────────
  function onTouchStart(e) {
    // iOS: cria contexto + toca buffer silencioso no mesmo gesto
    _ensureUnlocked();

    const el = e.target?.closest(BTN_SELECTORS);
    if (!el || el.disabled || el.getAttribute('aria-disabled') === 'true') return;

    _lastTouch = Date.now();
    const profile = getProfile(el);
    press(el, profile);

    // Android real vibration
    if (!isIOS) haptic[profile === 'heavy' ? 'heavy' : profile === 'light' ? 'light' : 'medium']?.();
  }

  // ── touchend / cancel ──────────────────────────────────────────────────────
  function onTouchEnd(e) {
    const el = e.target?.closest(BTN_SELECTORS);
    if (el) release(el);

    // Cleanup any stuck elements
    document.querySelectorAll('[style*="scale"]').forEach(node => {
      if (_pressed.has(node)) release(node);
    });
  }

  // ── mousedown / mouseup (desktop) ──────────────────────────────────────────
  function onMouseDown(e) {
    // Ignora mousedown gerado por touch (iOS dispara os dois; sourceCapabilities só existe no Chrome)
    if (e.sourceCapabilities?.firesTouchEvents) return;
    if (Date.now() - _lastTouch < TOUCH_MOUSE_GAP) return;
    _ensureUnlocked();
    const el = e.target?.closest(BTN_SELECTORS);
    if (!el || el.disabled || el.getAttribute('aria-disabled') === 'true') return;
    const profile = getProfile(el);
    press(el, profile);
  }
  function onMouseUp(e) {
    if (e.sourceCapabilities?.firesTouchEvents) return;
    const el = e.target?.closest(BTN_SELECTORS);
    if (el) release(el);
  }

  document.addEventListener('touchstart',  onTouchStart, { passive: true });
  document.addEventListener('touchend',    onTouchEnd,   { passive: true });
  document.addEventListener('touchcancel', onTouchEnd,   { passive: true });
  document.addEventListener('mousedown',   onMouseDown,  { passive: true });
  document.addEventListener('mouseup',     onMouseUp,    { passive: true });

  return () => {
    document.removeEventListener('touchstart',  onTouchStart);
    document.removeEventListener('touchend',    onTouchEnd);
    document.removeEventListener('touchcancel', onTouchEnd);
    document.removeEventListener('mousedown',   onMouseDown);
    document.removeEventListener('mouseup',     onMouseUp);
  };
}
